'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetMarketRow } from '@morfi/backend-sdk-v2/client';
import { useAddToWatchlist, useGetMarketField, useRemoveFromWatchlist } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import { ShareButton } from '@/components/market/share-button';
import { UserIdentity, type UserIdentityShape } from '@/components/profile/user-identity';
import { AnimatedBookmark } from '@/components/ui/animated-bookmark';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { ArrowLeft01Icon, ArrowRight01Icon } from '@/components/ui/icon/glyphs';
import { FEATURED_HERO_BOOKMARK_TOGGLED } from '@/events/featured-hero';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { trackEvent } from '@/lib/analytics';
import { useFormattedCurrency } from '@/lib/currency';
import { formatCount } from '@/lib/format/format-count';
import { cn } from '@/lib/ui/utils';
import type { HeroMarket } from './hero-market';
interface FeaturedHeaderProps {
  market: HeroMarket;
  /** Carousel slot index — propagated to telemetry events. */
  slot: number;
  className?: string;
  carousel?: FeaturedHeroCarouselNavProps;
  /** Page-owned market watchlist relationship, when this hero is on the feed. */
  watchlistPubkeys?: readonly string[] | undefined;
}
export interface FeaturedHeroCarouselNavProps {
  slides: ReadonlyArray<HeroMarket>;
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
}
type CreatorProfileField = GetMarketRow['creatorProfile'];
function toCreatorHandleIdentity(profile: CreatorProfileField): UserIdentityShape | null {
  if (profile === null || profile.handle === null || profile.handle.length === 0) {
    return null;
  }
  return {
    userId: profile.userId,
    walletAddress: profile.walletAddress,
    displayName: null,
    handle: profile.handle,
    avatarUrl: profile.avatarUrl,
    avatarNsfw: profile.avatarNsfw ?? false,
    // ALLOW-RASTER-LITERAL: creator profile may omit avatarPreferences; default matches non-raster avatar fallback.
    avatarPreferences: profile.avatarPreferences ?? {
      rasterise: false
    },
    connectedProviders: []
  };
}

/**
 * The hero bookmark control is split from the page-owned watchlist source so
 * carousel remounts reuse the feed relationship instead of refetching it.
 */
interface HeroBookmarkProps {
  pubkey: string;
  title: string;
  slot: number;
  watchlistPubkeys?: readonly string[] | undefined;
}
function HeroBookmarkControl(props) {
  const $ = _c(15);
  const {
    pubkey,
    title,
    slot,
    isSaved
  } = props;
  const tFeatured = useTranslations("markets.featured");
  const {
    ensureAuthenticated
  } = useAuthGate();
  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();
  let t0;
  if ($[0] !== addToWatchlist || $[1] !== ensureAuthenticated || $[2] !== isSaved || $[3] !== pubkey || $[4] !== removeFromWatchlist || $[5] !== slot) {
    t0 = function handleBookmarkClick(e) {
      e.stopPropagation();
      if (!ensureAuthenticated()) {
        return;
      }
      const willBeSaved = !isSaved;
      if (isSaved) {
        removeFromWatchlist.mutate({
          kind: "markets",
          pubkey
        });
      } else {
        addToWatchlist.mutate({
          kind: "markets",
          pubkey
        });
      }
      trackEvent(FEATURED_HERO_BOOKMARK_TOGGLED.name, {
        marketPubkey: pubkey,
        saved: willBeSaved,
        slot
      });
    };
    $[0] = addToWatchlist;
    $[1] = ensureAuthenticated;
    $[2] = isSaved;
    $[3] = pubkey;
    $[4] = removeFromWatchlist;
    $[5] = slot;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const handleBookmarkClick = t0;
  let t1;
  if ($[7] !== isSaved || $[8] !== tFeatured || $[9] !== title) {
    t1 = isSaved ? tFeatured("bookmark_remove_aria", {
      title
    }) : tFeatured("bookmark_save_aria", {
      title
    });
    $[7] = isSaved;
    $[8] = tFeatured;
    $[9] = title;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== handleBookmarkClick || $[12] !== isSaved || $[13] !== t1) {
    t2 = <AnimatedBookmark isSaved={isSaved} onClick={handleBookmarkClick} ariaLabel={t1} size={16} />;
    $[11] = handleBookmarkClick;
    $[12] = isSaved;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
function HeroBookmarkWithQuery(props) {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      kind: "markets",
      fields: ["pubkeys"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: watchlist
  } = useAuthenticatedWatchlist(t0);
  let t1;
  if ($[1] !== props.pubkey || $[2] !== watchlist?.pubkeys) {
    t1 = watchlist?.pubkeys?.includes(props.pubkey) ?? false;
    $[1] = props.pubkey;
    $[2] = watchlist?.pubkeys;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props || $[5] !== t1) {
    t2 = <HeroBookmarkControl {...props} isSaved={t1} />;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function HeroBookmark(props) {
  const $ = _c(11);
  let bookmarkProps;
  let watchlistPubkeys;
  if ($[0] !== props) {
    ({
      watchlistPubkeys,
      ...bookmarkProps
    } = props);
    $[0] = props;
    $[1] = bookmarkProps;
    $[2] = watchlistPubkeys;
  } else {
    bookmarkProps = $[1];
    watchlistPubkeys = $[2];
  }
  if (watchlistPubkeys === undefined) {
    let t0;
    if ($[3] !== bookmarkProps) {
      t0 = <HeroBookmarkWithQuery {...bookmarkProps} />;
      $[3] = bookmarkProps;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    return t0;
  }
  let t0;
  if ($[5] !== props.pubkey || $[6] !== watchlistPubkeys) {
    t0 = watchlistPubkeys.includes(props.pubkey);
    $[5] = props.pubkey;
    $[6] = watchlistPubkeys;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  let t1;
  if ($[8] !== bookmarkProps || $[9] !== t0) {
    t1 = <HeroBookmarkControl {...bookmarkProps} isSaved={t0} />;
    $[8] = bookmarkProps;
    $[9] = t0;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
function HeroCarouselNav(props) {
  const $ = _c(11);
  const {
    slides,
    currentIndex,
    onPrev,
    onNext
  } = props;
  const tFeatured = useTranslations("markets.featured");
  if (slides.length <= 1) {
    return null;
  }
  const slideCount = slides.length;
  const prevIndex = (currentIndex - 1 + slideCount) % slideCount;
  const nextIndex = (currentIndex + 1) % slideCount;
  const prevSlide = slides[prevIndex];
  const nextSlide = slides[nextIndex];
  let t0;
  if ($[0] !== onPrev || $[1] !== prevSlide || $[2] !== tFeatured) {
    t0 = prevSlide !== undefined ? <AnimatedButton variant="tertiary" size="sm" aria-label={tFeatured("carousel_prev_aria", {
      title: prevSlide.title
    })} className="size-8 p-0 text-foreground-tertiary" onClick={onPrev} data-testid="featured-carousel-prev"><Icon icon={ArrowLeft01Icon} size={18} /></AnimatedButton> : null;
    $[0] = onPrev;
    $[1] = prevSlide;
    $[2] = tFeatured;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== nextSlide || $[5] !== onNext || $[6] !== tFeatured) {
    t1 = nextSlide !== undefined ? <AnimatedButton variant="tertiary" size="sm" aria-label={tFeatured("carousel_next_aria", {
      title: nextSlide.title
    })} className="size-8 p-0 text-foreground-tertiary" onClick={onNext} data-testid="featured-carousel-next"><Icon icon={ArrowRight01Icon} size={18} /></AnimatedButton> : null;
    $[4] = nextSlide;
    $[5] = onNext;
    $[6] = tFeatured;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t0 || $[9] !== t1) {
    t2 = <div className="flex items-center" data-testid="featured-carousel-nav">{t0}{t1}</div>;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
export function FeaturedHeader(props) {
  const $ = _c(57);
  const {
    market,
    slot,
    className,
    carousel,
    watchlistPubkeys
  } = props;
  const tFeatured = useTranslations("markets.featured");
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  const liveUniqueTraders = useGetMarketField({
    pubkey: market.pubkey
  }, "uniqueTradersTotal") ?? market.uniqueTradersTotal ?? 0;
  const liveVolumeTotal = useGetMarketField({
    pubkey: market.pubkey
  }, "volumeTotal") ?? market.volumeTotal;
  const livePrizePool = useGetMarketField({
    pubkey: market.pubkey
  }, "prizePool") ?? market.prizePool ?? "0";
  let t0;
  if ($[0] !== market.pubkey) {
    t0 = {
      pubkey: market.pubkey
    };
    $[0] = market.pubkey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const liveCreatorProfile = useGetMarketField(t0, "creatorProfile");
  let t1;
  if ($[2] !== liveCreatorProfile) {
    t1 = liveCreatorProfile !== null && liveCreatorProfile !== undefined ? toCreatorHandleIdentity(liveCreatorProfile) : null;
    $[2] = liveCreatorProfile;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const creatorUser = t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("flex flex-col gap-1", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== market.title) {
    t3 = <h2 className="min-w-0 flex-1 truncate text-left text-heading-lg leading-[28px] font-semibold tracking-[-0.4px] text-foreground-primary" data-testid="featured-title-heading">{market.title}</h2>;
    $[6] = market.title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== market.pubkey || $[9] !== market.title || $[10] !== slot || $[11] !== watchlistPubkeys) {
    t4 = <HeroBookmark pubkey={market.pubkey} title={market.title} slot={slot} watchlistPubkeys={watchlistPubkeys} />;
    $[8] = market.pubkey;
    $[9] = market.title;
    $[10] = slot;
    $[11] = watchlistPubkeys;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== market.pubkey || $[14] !== market.title) {
    t5 = <ShareButton marketPubkey={market.pubkey} marketTitle={market.title} iconSize={16} />;
    $[13] = market.pubkey;
    $[14] = market.title;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== carousel) {
    t6 = carousel !== undefined && carousel.slides.length > 1 ? <><span aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-border-primary" data-testid="featured-carousel-nav-divider" /><HeroCarouselNav {...carousel} /></> : null;
    $[16] = carousel;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <div className="relative z-10 -mr-2 flex shrink-0 items-center gap-0">{t4}{t5}{t6}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t7) {
    t8 = <div className="flex items-center gap-3">{t3}{t7}</div>;
    $[22] = t3;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== locale || $[26] !== tFeatured) {
    t9 = v => tFeatured("meta_traders", {
      count: formatCount(v, locale)
    });
    $[25] = locale;
    $[26] = tFeatured;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== liveUniqueTraders || $[29] !== t9) {
    t10 = <span data-testid="featured-meta-traders"><AnimatedNumber value={liveUniqueTraders} format={t9} /></span>;
    $[28] = liveUniqueTraders;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span aria-hidden="true">{" \xB7 "}</span>;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== formatD6 || $[33] !== liveVolumeTotal || $[34] !== tFeatured) {
    t12 = tFeatured("meta_volume", {
      amount: formatD6(liveVolumeTotal, "compactDecimal")
    });
    $[32] = formatD6;
    $[33] = liveVolumeTotal;
    $[34] = tFeatured;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12) {
    t13 = <span data-testid="featured-meta-volume">{t12}</span>;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span aria-hidden="true">{" \xB7 "}</span>;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== formatD6 || $[40] !== livePrizePool || $[41] !== tFeatured) {
    t15 = tFeatured("meta_prize_pool", {
      amount: formatD6(livePrizePool, "compactDecimal")
    });
    $[39] = formatD6;
    $[40] = livePrizePool;
    $[41] = tFeatured;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = <span data-testid="featured-meta-prize">{t15}</span>;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== creatorUser || $[46] !== tFeatured) {
    t17 = creatorUser !== null ? <><span aria-hidden="true">{" \xB7 "}</span><span className="inline-flex items-center gap-1" data-testid="featured-meta-creator"><span>{tFeatured("meta_creator")}</span><UserIdentity user={creatorUser} variant="inline" size="sm" showProviderBadges={false} /></span></> : null;
    $[45] = creatorUser;
    $[46] = tFeatured;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t10 || $[49] !== t13 || $[50] !== t16 || $[51] !== t17) {
    t18 = <p className="flex flex-wrap items-center gap-x-1 text-caption text-foreground-secondary" data-testid="featured-meta">{t10}{t11}{t13}{t14}{t16}{t17}</p>;
    $[48] = t10;
    $[49] = t13;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t18 || $[54] !== t2 || $[55] !== t8) {
    t19 = <div className={t2} data-testid="featured-header">{t8}{t18}</div>;
    $[53] = t18;
    $[54] = t2;
    $[55] = t8;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  return t19;
}